import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';

import { requireAuthentication } from '../utils/authUtils';
import { saveLetterData, getUserIPAddress } from '../utils/profilePhotoUtils';
import { createLetterToHeaven, updateLetterToHeaven } from '../api';
import '../styles/LetterToHeavenWritingPC.css';
import FamilyPlazaSubHeading from './family-plaza/FamilyPlazaSubHeading';
import FamilyPlazaTopSection from './family-plaza/FamilyPlazaTopSection';

const LetterToHeavenWritingPC = () => {
  const navigate = useNavigate();
  const handleNavigation = (route) => {
    // Check authentication before navigation
    if (!requireAuthentication()) {
      return; // requireAuthentication will handle the alert and redirect
    }
    navigate(route);
  };

  // Form state
  const [recipient, setRecipient] = useState('');
  const [sender, setSender] = useState('');
  const [password, setPassword] = useState('');
  const [selectedStamp, setSelectedStamp] = useState('stamp-1');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');

  // No authentication state needed for letter to heaven

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editingLetterId, setEditingLetterId] = useState(null);

  // Check if we're in edit mode and load existing data
  useEffect(() => {
    const editingData = localStorage.getItem('editingLetterToHeavenDataPC');
    if (editingData) {
      try {
        const parsedData = JSON.parse(editingData);
        setRecipient(parsedData.recipient || '');
        setSender(parsedData.sender || '');
        setPassword(parsedData.password || '');
        setSelectedStamp(parsedData.selectedStamp || 'stamp-1');
        setTitle(parsedData.title || '');
        setContent(parsedData.content || '');
        setEditingLetterId(parsedData.id);
        setIsEditing(true);
      } catch (error) {
        console.error('Error parsing editing data:', error);
      }
    }
  }, []);

  const handleSubmit = async () => {
    // Validate required fields
    if (!recipient.trim()) {
      alert('받으시는 분을 입력해주세요.');
      return;
    }
    if (!sender.trim()) {
      alert('보내시는 분을 입력해주세요.');
      return;
    }
    if (!password.trim()) {
      alert('비밀번호를 입력해주세요.');
      return;
    }
    if (!title.trim()) {
      alert('제목을 입력해주세요.');
      return;
    }
    if (!content.trim()) {
      alert('내용을 입력해주세요.');
      return;
    }

    // Proceed with form submission directly (no authentication modal for letter to heaven)
    await saveLetterDataToAPI();
    navigate('/letter-to-heaven-board-list');
  };

  // Remove authentication modal handlers since they're not needed for letter to heaven

  // Remove authentication modal handlers since they're not needed for letter to heaven

  const saveLetterDataToAPI = async () => {
    const mapSelectedStampToBoardStamp = (stampId) => {
      // Extract the number from stamp-X and return it directly
      const match = stampId.match(/stamp-(\d+)/);
      if (match) {
        return parseInt(match[1], 10); // Returns 1, 2, 3, 4, 5, 6, 7
      }
      return 1; // Default to stamp-1
    };
    try {
      // Get user's IP address
      const userIP = await getUserIPAddress();

      // Prepare letter data for API (expects userName, toName, and boardStamp)
      const letterData = {
        title: title,
        content: content,
        password: password,
        secureFlag: 'Y',
        ip: userIP,
        userName: sender,
        toName: recipient,
        boardStamp: mapSelectedStampToBoardStamp(selectedStamp),
      };

      let response;
      if (isEditing && editingLetterId) {
        // Update existing letter - include ID in the data
        const updateData = {
          id: editingLetterId,
          title: title,
          content: content,
          password: password,
          flowerImageUrl: selectedStamp // Send the stamp ID as flowerImageUrl
        };
        console.log('🔄 UPDATING LETTER - ID:', editingLetterId);
        console.log('🔄 UPDATING LETTER - Title:', title);
        console.log('🔄 UPDATING LETTER - Content:', content);
        console.log('🔄 UPDATING LETTER - Sender:', sender);
        console.log('🔄 UPDATING LETTER - Recipient:', recipient);
        console.log('🔄 UPDATING LETTER - SelectedStamp:', selectedStamp);
        console.log('🔄 UPDATING LETTER - API Data:', updateData);
        response = await updateLetterToHeaven(updateData);
        console.log('✅ LETTER UPDATED - API Response:', response.data);
      } else {
        // Create new letter
        console.log('🔄 CREATING LETTER - Sender:', sender);
        console.log('🔄 CREATING LETTER - Recipient:', recipient);
        console.log('🔄 CREATING LETTER - Title:', title);
        console.log('🔄 CREATING LETTER - Content:', content);
        console.log('🔄 CREATING LETTER - SelectedStamp:', selectedStamp);
        console.log('🔄 CREATING LETTER - API Data:', letterData);
        response = await createLetterToHeaven(letterData);
        console.log('✅ LETTER CREATED - API Response:', response.data);
      }

      // Also save to localStorage for cross-platform sync
      const localLetterData = {
        recipient: recipient,
        sender: sender,
        password: password,
        selectedStamp: selectedStamp,
        title: title,
        content: content,
        date: new Date().toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\. /g, '.').replace(/\.$/, ''),
        id: isEditing ? editingLetterId : (response.data?.id || response.data?.letterId || Date.now())
      };

      console.log('💾 SAVING TO LOCALSTORAGE - Data:', localLetterData);
      console.log('💾 SAVING TO LOCALSTORAGE - Is Editing:', isEditing);
      console.log('💾 SAVING TO LOCALSTORAGE - Letter ID:', localLetterData.id);

      saveLetterData(localLetterData, 'PC');

      // Clear editing data if in edit mode
      if (isEditing) {
        localStorage.removeItem('editingLetterToHeavenDataPC');
        localStorage.removeItem('selectedLetterToHeavenDataPC');
      }
    } catch (error) {
      console.error('❌ Error saving letter to API:', error);

      // Handle 401 errors gracefully (don't redirect)
      if (error.response && error.response.status === 401) {
        console.warn('⚠️ Authentication required for API, using localStorage fallback');
      }

      // Fallback to localStorage only if API fails
      const fallbackData = {
        recipient: recipient,
        sender: sender,
        password: password,
        selectedStamp: selectedStamp,
        title: title,
        content: content,
        date: new Date().toISOString().split('T')[0],
        id: isEditing ? editingLetterId : Date.now()
      };
      saveLetterData(fallbackData, 'PC');
    }
  };

  const handleStampChange = (stampId) => {
    setSelectedStamp(stampId);
  };

  const handleCancel = () => {
    // Clear editing data if in edit mode
    if (isEditing) {
      localStorage.removeItem('editingLetterToHeavenDataPC');
      localStorage.removeItem('selectedLetterToHeavenDataPC');
    }

    navigate('/letter-to-heaven-board-list');
  };

  return (

    <div className="letter-to-heaven-board-list-pc page-letter-to-heaven-board-list-pc announcement-list-page donate-info-page-pc">
      <FamilyPlazaTopSection />



      <main className="main-content">
        <FamilyPlazaSubHeading heading="하늘로 보내는 편지" />


        {/* Main Content */}
        <div className="letter-to-heaven-writing-main-content">
          <div className="letter-to-heaven-writing-page-content">
            <div className="letter-to-heaven-writing-container">

              {/* Content Area - Letter to Heaven Writing Form */}
              <div className="letter-to-heaven-writing-content-area">

                {/* Writing Form */}
                <div className="letter-to-heaven-writing-form">

                  {/* First Row - Recipient and Sender Fields */}
                  <div className="letter-to-heaven-writing-form-row">
                    <div className="letter-to-heaven-writing-form-field">
                      <label className="letter-to-heaven-writing-form-label">받으시는 분 <span className="letter-to-heaven-writing-asterisk">*</span></label>
                      <input
                        type="text"
                        className="letter-to-heaven-writing-form-input"
                        value={recipient}
                        onChange={(e) => setRecipient(e.target.value)}
                      />
                    </div>
                    <div className="letter-to-heaven-writing-form-field">
                      <label className="letter-to-heaven-writing-form-label">보내시는 분 <span className="letter-to-heaven-writing-asterisk">*</span></label>
                      <input
                        type="text"
                        className="letter-to-heaven-writing-form-input"
                        value={sender}
                        onChange={(e) => setSender(e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Password Field */}
                  <div className="letter-to-heaven-writing-form-row">
                    <div className="letter-to-heaven-writing-form-field">
                      <label className="letter-to-heaven-writing-form-label">비밀번호 <span className="letter-to-heaven-writing-asterisk">*</span></label>
                      <input
                        type="password"
                        className="letter-to-heaven-writing-form-input"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                    </div>
                    <div className="letter-to-heaven-writing-form-hint">
                      <span className="letter-to-heaven-writing-form-hint-text">영문+숫자 10자리 미만</span>
                    </div>
                  </div>

                  {/* Stamp Image Selection */}
                  <div className="letter-to-heaven-writing-stamp-section">
                    <div className="letter-to-heaven-writing-section-label">
                      <label className="letter-to-heaven-writing-stamp-label">우표 이미지 <span className="letter-to-heaven-writing-asterisk">*</span></label>
                    </div>
                    <div className="letter-to-heaven-writing-stamp-options">
                      {[
                        { id: 1, image: '/heaven/getty-images-145244798.png' },
                        { id: 2, image: '/heaven/getty-images-465710299.png' },
                        { id: 3, image: '/heaven/getty-images-955651052.png' },
                        { id: 4, image: '/heaven/getty-images-907936742.png' },
                        { id: 5, image: '/heaven/getty-images-494124656.png' },
                        { id: 6, image: '/heaven/getty-images-926601494.png' },
                        { id: 7, image: '/heaven/getty-images-537807854.png' }
                      ].map((stamp) => (
                        <div key={stamp.id} className="letter-to-heaven-writing-stamp-option">
                          <input
                            type="radio"
                            name="stamp-selection-pc"
                            id={`stamp-${stamp.id}-pc`}
                            className="letter-to-heaven-writing-stamp-radio"
                            checked={selectedStamp === `stamp-${stamp.id}`}
                            onChange={() => handleStampChange(`stamp-${stamp.id}`)}
                          />
                          <label htmlFor={`stamp-${stamp.id}-pc`} className="letter-to-heaven-writing-stamp-option-label">
                            <img
                              src={stamp.image}
                              alt={`Stamp ${stamp.id}`}
                              onError={(e) => {
                                e.target.src = '/heaven/getty-images-145244798.png';
                              }}
                            />
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Title Field */}
                  <div className="letter-to-heaven-writing-form-row">
                    <div className="letter-to-heaven-writing-form-field">
                      <label className="letter-to-heaven-writing-form-label">제목 <span className="letter-to-heaven-writing-asterisk">*</span></label>
                      <input
                        type="text"
                        className="letter-to-heaven-writing-form-input"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Content Area */}
                  <div className="letter-to-heaven-writing-form-row">
                    <div className="letter-to-heaven-writing-form-field">
                      <label className="letter-to-heaven-writing-form-label">내용 <span className="letter-to-heaven-writing-asterisk">*</span></label>
                      <textarea
                        className="letter-to-heaven-writing-form-textarea"
                        placeholder="내용을 입력해주세요."
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                      ></textarea>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="letter-to-heaven-writing-form-actions">
                    <button className="letter-to-heaven-writing-form-cancel-btn" onClick={handleCancel}>취소</button>
                    <button className="letter-to-heaven-writing-form-submit-btn" onClick={handleSubmit}>
                      {isEditing ? '수정' : '등록'}
                    </button>
                  </div>

                  {/* Notice Text */}
                  <div className="letter-to-heaven-writing-notice">
                    <p>* 고인을 기억하는 이야기는 누군가에게 큰 위로가 됩니다.</p>
                    <p>본 게시판에 남겨주신 글은 강화 파라다이스 공식 블로그에 소개될 수 있으며,</p>
                    <p>이 경우 성함 등 개인정보는 공개되지 않습니다. 글이 외부에 노출되는 것을 원하지 않으시는 경우, 작성 후 비밀글로 설정해주세요.</p>
                  </div>

                </div>

              </div>

            </div>
          </div>
        </div>
      </main>


      {/* No authentication modal for letter to heaven */}
    </div>
  );
};

export default LetterToHeavenWritingPC; 